import React, { useState, useEffect } from 'react';
import {
  getEnterprises, createEnterprise, updateEnterprise,
  activateEnterprise, deactivateEnterprise, deleteEnterprise,
  getDimensions,
} from '../../api/admin';
import AdminLayout from '../../components/AdminLayout';
import Button from '../../components/Button';
import Alert from '../../components/Alert';
import AdminEnterpriseClassesSection from '../../components/admin/AdminEnterpriseClassesSection';

// 测评深度档位 — V5 题量与轮数硬编码于 backend/apps/assessment/round_plan.py,
// 与 AIStrategyConfig 解耦。任何企业、任何策略走同一份固定结构。
const V5_DEPTH_OPTIONS = [
  {
    value: 'quick',
    title: '快速',
    spec: '30 题 / 3 轮能力 + 1 批素质 / 约 18 分钟',
    note: 'R3 + 素质批 共 14 题 AI 出题',
  },
  {
    value: 'standard',
    title: '标准',
    spec: '60 题 / 6 轮能力 + 2 批素质 / 约 36 分钟',
    note: 'R3/R6 + 2 批素质 共 28 题 AI 出题',
  },
  {
    value: 'deep',
    title: '深度',
    spec: '90 题 / 9 轮能力 + 3 批素质 / 约 54 分钟',
    note: 'R3/R6/R9 + 3 批素质 共 42 题 AI 出题',
  },
];

function buildDepthOptions() {
  return V5_DEPTH_OPTIONS;
}

const EMPTY_FORM = { name: '', code: '', industry: '', rules: {} };

const DEFAULT_RULES = {
  depth: 'standard',
  enabled_dimensions: null, // null = 全部
  require_complete: false,
};

export default function AdminEnterprisesPage() {
  const [list, setList] = useState([]);
  const [dimensions, setDimensions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [filterActive, setFilterActive] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterActive !== '') params.is_active = filterActive;
      const { data } = await getEnterprises(params);
      const rows = data.results ?? data;
      setList(rows);
      // 若正在编辑某企业，用最新行同步 editItem —— 团队管理区据此拿到最新
      // classes_enabled / employee_count（启用/转无团队后立即切换形态）。
      setEditItem((cur) => (cur ? (rows.find((r) => r.id === cur.id) ?? cur) : cur));
    } catch { setError('加载失败'); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    getDimensions({ is_active: true }).then(({ data }) => setDimensions(data.results ?? data)).catch(() => {});
  }, [filterActive]); // eslint-disable-line

  const openCreate = () => {
    setEditItem(null);
    setForm({ ...EMPTY_FORM, rules: { ...DEFAULT_RULES } });
    setShowModal(true);
  };
  const openEdit = (e) => {
    setEditItem(e);
    const oldRules = e.rules || {};
    setForm({
      name: e.name,
      code: e.code,
      industry: e.industry || '',
      reports_viewable_by_employees: e.reports_viewable_by_employees !== false, // 缺省视为 true
      rules: {
        depth: oldRules.depth || 'standard',
        enabled_dimensions: oldRules.enabled_dimensions ?? null,
        require_complete: !!oldRules.require_complete,
      },
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.code.trim()) { setError('名称和企业码不能为空'); return; }
    setSaving(true); setError('');
    try {
      if (editItem) await updateEnterprise(editItem.id, form);
      else await createEnterprise(form);
      setSuccess(editItem ? '修改成功' : '创建成功');
      setShowModal(false); load();
    } catch (e) {
      setError(e.response?.data?.detail || JSON.stringify(e.response?.data) || '保存失败');
    } finally { setSaving(false); }
  };

  const handleActivate = async (e) => {
    try { await activateEnterprise(e.id); setSuccess(`已激活：${e.name}`); load(); }
    catch (err) { setError(err.response?.data?.detail || '操作失败'); }
  };

  const handleDeactivate = async (e) => {
    if (!window.confirm(`确认停用 ${e.name}？`)) return;
    try { await deactivateEnterprise(e.id); setSuccess(`已停用：${e.name}`); load(); }
    catch (err) { setError(err.response?.data?.detail || '操作失败'); }
  };

  const handleDelete = async (e) => {
    if (!window.confirm(`确认删除企业 ${e.name}？此操作不可恢复！`)) return;
    try { await deleteEnterprise(e.id); setSuccess('已删除'); load(); }
    catch { setError('删除失败'); }
  };

  const setRule = (key, value) => setForm(f => ({ ...f, rules: { ...f.rules, [key]: value } }));

  return (
    <AdminLayout title="企业管理">
      {error && <Alert type="error" style={{ marginBottom: 12 }}>{error}</Alert>}
      {success && <Alert type="success" style={{ marginBottom: 12 }}>{success}</Alert>}

      {/* 工具栏 */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center' }}>
        <select value={filterActive} onChange={e => setFilterActive(e.target.value)}
          style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13 }}>
          <option value="">全部状态</option>
          <option value="true">已激活</option>
          <option value="false">待审核</option>
        </select>
        <Button size="sm" style={{ marginLeft: 'auto' }} onClick={openCreate}>+ 新增企业</Button>
      </div>

      {/* 企业列表 */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {loading ? <p style={{ color: '#94a3b8' }}>加载中...</p> :
          list.length === 0 ? <p style={{ color: '#94a3b8', textAlign: 'center', padding: 32 }}>暂无数据</p> :
          list.map(e => (
            <div key={e.id} style={{
              background: '#fff', borderRadius: 12, padding: '18px 24px',
              border: `1px solid ${e.is_active ? '#e2e8f0' : '#fde68a'}`,
              display: 'flex', alignItems: 'center', gap: 16,
            }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                  <span style={{ fontWeight: 700, fontSize: 15 }}>{e.name}</span>
                  <code style={{ fontSize: 11, background: '#f1f5f9', padding: '2px 8px', borderRadius: 6, color: '#475569' }}>
                    {e.code}
                  </code>
                  <span style={{
                    fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 6,
                    background: e.is_active ? '#f0fdf4' : '#fffbeb',
                    color: e.is_active ? '#16a34a' : '#d97706',
                  }}>
                    {e.is_active ? '已激活' : '待审核'}
                  </span>
                </div>
                <div style={{ fontSize: 12, color: '#94a3b8' }}>
                  {e.industry && <span style={{ marginRight: 8 }}>行业：{e.industry} ·</span>}
                  创建于 {e.created_at ? new Date(e.created_at).toLocaleDateString('zh-CN') : '—'}
                  {e.created_by_email && ` · 申请人：${e.created_by_email}`}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                {!e.is_active && (
                  <button onClick={() => handleActivate(e)} style={actionBtn('#16a34a')}>激活</button>
                )}
                {e.is_active && (
                  <button onClick={() => handleDeactivate(e)} style={actionBtn('#d97706')}>停用</button>
                )}
                <button onClick={() => openEdit(e)} style={actionBtn('#2563eb')}>编辑规则</button>
                <button onClick={() => handleDelete(e)} style={actionBtn('#dc2626')}>删除</button>
              </div>
            </div>
          ))
        }
      </div>

      {/* 编辑弹窗 */}
      {showModal && (
        <Modal title={editItem ? `编辑企业：${editItem.name}` : '新增企业'} onClose={() => setShowModal(false)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={labelStyle}>企业名称 *</label>
                <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>企业码 * (4~8位字母数字)</label>
                <input value={form.code} onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))}
                  disabled={!!editItem} style={{ ...inputStyle, background: editItem ? '#f8fafc' : '#fff' }} />
              </div>
            </div>
            <div>
              <label style={labelStyle}>所属行业（可选，用于信息预填和题目标签优先）</label>
              <input value={form.industry} onChange={e => setForm(f => ({ ...f, industry: e.target.value }))}
                placeholder="如：互联网、金融、教育" style={inputStyle} />
            </div>

            {/* ─── 测评规则 ─── */}
            <div style={sectionStyle}>
              <div style={sectionTitle}>测评规则</div>

              {/* 测评深度 — 文案随当前选中 strategy 联动 */}
              <div style={{ marginBottom: 14 }}>
                <label style={labelStyle}>测评深度</label>
                <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 6 }}>
                  V5 题量与轮数已由后端 round_plan.py 硬编码（与策略解耦）。
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
                  {buildDepthOptions().map(opt => {
                    const checked = (form.rules.depth || 'standard') === opt.value;
                    return (
                      <label key={opt.value} style={depthOptionStyle(checked)}>
                        <input
                          type="radio"
                          name="depth"
                          checked={checked}
                          onChange={() => setRule('depth', opt.value)}
                          style={{ marginTop: 2 }}
                        />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 600, fontSize: 13, color: '#1e293b' }}>{opt.title}</div>
                          <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{opt.spec}</div>
                          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1 }}>{opt.note}</div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* 强制完成 */}
              <label style={{ ...inlineCheckStyle, marginTop: 10 }}>
                <input type="checkbox" checked={!!form.rules.require_complete}
                  onChange={e => setRule('require_complete', e.target.checked)} />
                <span>
                  <div style={{ fontSize: 13, color: '#374151', fontWeight: 500 }}>强制完成</div>
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1 }}>
                    员工不能中途退出测评（关闭浏览器再进入会回到上次断点）
                  </div>
                </span>
              </label>

              {/* 员工可查看本人报告（仅企业测评受控；超管设置） */}
              <label style={{ ...inlineCheckStyle, marginTop: 10 }}>
                <input type="checkbox"
                  checked={form.reports_viewable_by_employees !== false}
                  onChange={e => setForm(f => ({ ...f, reports_viewable_by_employees: e.target.checked }))} />
                <span>
                  <div style={{ fontSize: 13, color: '#374151', fontWeight: 500 }}>允许员工查看本人报告</div>
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1 }}>
                    取消勾选后，该企业普通员工完成测评将看不到自己的报告（仅提示+返回首页）；
                    企业管理员/超管查看与报告生成不受影响。
                  </div>
                </span>
              </label>

              {/* 启用维度 */}
              <div style={{ marginTop: 14 }}>
                <label style={labelStyle}>启用维度</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 4 }}>
                  <label style={inlineCheckStyle}>
                    <input type="radio" name="dim-mode"
                      checked={form.rules.enabled_dimensions == null}
                      onChange={() => setRule('enabled_dimensions', null)} />
                    <span style={{ fontSize: 13, color: '#374151' }}>全部启用（{dimensions.length} 个）</span>
                  </label>
                  <label style={inlineCheckStyle}>
                    <input type="radio" name="dim-mode"
                      checked={Array.isArray(form.rules.enabled_dimensions)}
                      onChange={() => setRule('enabled_dimensions', dimensions.map(d => d.id))} />
                    <span style={{ fontSize: 13, color: '#374151' }}>仅启用指定维度</span>
                  </label>
                </div>

                {Array.isArray(form.rules.enabled_dimensions) && (
                  <div style={{
                    display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 6,
                    marginTop: 8, padding: '10px 12px',
                    background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0',
                  }}>
                    {dimensions.map(d => {
                      const sel = form.rules.enabled_dimensions.includes(d.id);
                      return (
                        <label key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer' }}>
                          <input type="checkbox" checked={sel}
                            onChange={(e) => {
                              const cur = form.rules.enabled_dimensions || [];
                              setRule('enabled_dimensions',
                                e.target.checked ? [...cur, d.id] : cur.filter(x => x !== d.id));
                            }} />
                          <span style={{ color: '#475569' }}>{d.name}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* ─── 团队管理（class-stream A4，仅编辑已有企业时显示）─── */}
            {editItem && (
              <AdminEnterpriseClassesSection enterprise={editItem} onChange={load} />
            )}

            {error && <Alert type="error">{error}</Alert>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <Button variant="secondary" size="sm" onClick={() => setShowModal(false)}>取消</Button>
              <Button size="sm" onClick={handleSave} loading={saving}>保存</Button>
            </div>
          </div>
        </Modal>
      )}
    </AdminLayout>
  );
}

const actionBtn = (color) => ({
  padding: '6px 14px', borderRadius: 8, border: `1px solid ${color}30`,
  background: `${color}0f`, color, fontSize: 12, cursor: 'pointer', fontWeight: 500,
});
const inputStyle = { padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13, width: '100%', boxSizing: 'border-box' };
const labelStyle = { fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 };
const sectionStyle = { padding: '14px 16px', background: '#fafbfc', borderRadius: 10, border: '1px solid #e2e8f0' };
const sectionTitle = { fontSize: 13, fontWeight: 700, color: '#1e293b', marginBottom: 12, paddingBottom: 8, borderBottom: '1px solid #e2e8f0' };
const inlineCheckStyle = { display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer' };
const depthOptionStyle = (checked) => ({
  display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer',
  padding: '10px 12px', borderRadius: 8,
  border: `1.5px solid ${checked ? '#2563eb' : '#e2e8f0'}`,
  background: checked ? '#eff6ff' : '#fff',
});

function Modal({ title, onClose, children }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 100, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 20px', overflowY: 'auto' }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: 28, width: '100%', maxWidth: 600, maxHeight: '85vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>{title}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#94a3b8' }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}
